import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  Fade,
  Grid,
  Avatar,
  Skeleton
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ShowChart,
  LoginOutlined,
  Savings,
} from "@mui/icons-material";
import { getMonthsLongNames, getMonthIndex, getFirstDayOfMonth, getLastDayOfMonth } from "../../../utils/DateUtil";
import { useAuth } from "../../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../../utils/constants/AppConstants";
import { useMonthlyReport } from "../../../hooks/reports";
import ExpensesMonthlyReportsComponent from "../../../components/finance/reports/monthly/ExpensesMonthlyReportsComponent";
import IncomesMonthlyReportsComponent from "../../../components/finance/reports/monthly/IncomesMonthlyReportsComponent";
import { PageContainer, ModernCard, GradientButton } from "../../../components/common/StyledComponents";
import MonthYearPicker from "../../../components/common/MonthYearPicker";
import { useCurrencyFormatter } from "../../../contexts/CurrencyContext";
import { useError } from "../../../error/useError";
import ErrorAlert from "../../../components/common/ErrorAlert";

const MonthlyReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const monthNames = getMonthsLongNames();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = Number(searchParams.get("year")) || currentYear;
  const urlMonth = searchParams.get("month") || monthNames[currentMonthIndex];

  const [selectedYear, setSelectedYear] = useState(urlYear);
  const [selectedMonth, setSelectedMonth] = useState(urlMonth);
  const [loading, setLoading] = useState(false);
  const [dataKey, setDataKey] = useState(0); // For triggering transitions
  const { error, helpText, clearError, handleError } = useError();

  const updateSearchParams = useCallback((newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  }, [searchParams, setSearchParams]);

  const startDate = getFirstDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const endDate = getLastDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const { data: report } = useMonthlyReport(startDate, endDate);
  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      // React Query handles refetch; we just toggle loading for the UI affordance
    } catch (e) {
      handleError(e, "Failed to load report data");
    } finally {
      setLoading(false);
    }
  }, [clearError, handleError]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    updateSearchParams({ year: year.toString() });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateSearchParams({ month });
    setDataKey(prev => prev + 1); // Trigger transition
  };



  useEffect(() => {
    if (isAuthenticated) {
      fetchReport();
    }
  }, [fetchReport, isAuthenticated, selectedYear, selectedMonth]);

  useEffect(() => {
    if (isAuthenticated) {
      const needsInit = !searchParams.get("year") || !searchParams.get("month");
      if (needsInit) {
        updateSearchParams({
          year: selectedYear.toString(),
          month: selectedMonth,
        });
      }
    }
  }, [isAuthenticated, searchParams, selectedMonth, selectedYear, updateSearchParams]);

  // Calculate summary stats
  const totalExpenses = report?.reportInfo?.totalValues?.expensesTotalAmount || 0;
  const totalIncome = report?.reportInfo?.totalValues?.incomesTotalAmount || 0;
  const netBalance = totalIncome - totalExpenses;
  
  // Calculate total transactions from actual counts
  const expensesCount = report?.reportInfo?.totalValues?.expensesCount || 0;
  const incomesCount = report?.reportInfo?.totalValues?.incomesCount || 0;
  const totalTransactions = expensesCount + incomesCount;
  
  // Calculate savings rate (percentage of income saved)
  // Only calculate if there's income; otherwise show N/A
  const hasSavingsRate = totalIncome > 0;
  const savingsRate = hasSavingsRate ? ((netBalance / totalIncome) * 100) : 0;
  const savingsRateFormatted = savingsRate.toFixed(1);
  
  // Determine display for Savings Rate card
  const getSavingsRateDisplay = () => {
    // No data at all
    if (totalIncome === 0 && totalExpenses === 0) {
      return { 
        text: 'No Data', 
        status: 'neutral', 
        subtitle: 'Add transactions to track' 
      };
    }
    
    // Has expenses but no income - can't calculate a meaningful rate
    if (totalIncome === 0 && totalExpenses > 0) {
      return { 
        text: 'N/A', 
        status: 'insufficient',
        subtitle: 'No income to calculate rate'
      };
    }
    
    // Normal case: has income, show percentage
    const subtitle = savingsRate >= 20 
      ? '🎉 Excellent savings!' 
      : savingsRate >= 10 
      ? '👍 Good progress' 
      : savingsRate >= 0 
      ? '💪 Keep it up'
      : '⚠️ Overspending';
      
    return { 
      text: `${savingsRateFormatted}%`, 
      status: savingsRate >= 20 ? 'excellent' : savingsRate >= 0 ? 'good' : 'overspent',
      subtitle 
    };
  };
  
  const savingsDisplay = getSavingsRateDisplay();

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <ShowChart sx={{ fontSize: '4rem', color: theme.palette.primary.main, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Financial Reports
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              Get comprehensive insights into your financial health with detailed monthly reports, trends analysis, and budget comparisons.
            </Typography>
            <GradientButton
              variant="contained"
              startIcon={<LoginOutlined />}
              onClick={() => navigate(LOGIN_ROUTE_PATH)}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                px: 4,
                py: 1.5,
              }}
            >
              Sign In to Continue
            </GradientButton>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={true} timeout={400}>
          <Box>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <ShowChart sx={{ fontSize: '2.5rem', color: theme.palette.primary.main }} />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Monthly Reports
                </Typography>
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Comprehensive analysis of your financial activities and trends
              </Typography>
            </Box>

            {/* Period Selector */}
            <ModernCard sx={{ p: 2.5, mb: 4 }}>
              <MonthYearPicker
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                onMonthChange={handleMonthChange}
                onYearChange={handleYearChange}
                colorTheme="primary"
                transactionCount={totalTransactions}
                isLoading={loading}
              />
            </ModernCard>

            {/* Error Display */}
            <ErrorAlert
              error={error}
              helpText={helpText}
              onClear={clearError}
              onRetry={fetchReport}
              title="Report Error"
              sx={{ mb: 4 }}
            />

            {/* Summary Cards with Animation */}
            <Fade in={true} timeout={600} key={`summary-${dataKey}`}>
              <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} sm={6} md={3}>
                <ModernCard sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    }}
                  >
                    <TrendingDown sx={{ fontSize: '1.8rem', color: 'white' }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Total Expenses
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main, mb: 1 }}>
                    {loading ? <Skeleton width={100} /> : formatCurrency(totalExpenses)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    for {selectedMonth} {selectedYear}
                  </Typography>
                </ModernCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ModernCard sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    }}
                  >
                    <TrendingUp sx={{ fontSize: '1.8rem', color: 'white' }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Total Income
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                    {loading ? <Skeleton width={100} /> : formatCurrency(totalIncome)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    for {selectedMonth} {selectedYear}
                  </Typography>
                </ModernCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ModernCard sx={{ p: 4, textAlign: 'center', height: '100%' }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      background: netBalance === 0
                        ? `linear-gradient(135deg, ${theme.palette.grey[400]}, ${theme.palette.grey[600]})`
                        : netBalance > 0 
                        ? `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                        : `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    }}
                  >
                    <AccountBalance sx={{ fontSize: '1.8rem', color: 'white' }} />
                  </Avatar>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Net Balance
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700, 
                      color: netBalance === 0 
                        ? theme.palette.text.secondary
                        : netBalance > 0 
                        ? theme.palette.success.main 
                        : theme.palette.error.main,
                      mb: 1
                    }}
                  >
                    {loading ? (
                      <Skeleton width={100} />
                    ) : netBalance === 0 ? (
                      formatCurrency(netBalance)
                    ) : (
                      `${netBalance > 0 ? '+' : ''}${formatCurrency(netBalance)}`
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {netBalance === 0 ? 'Break even' : netBalance > 0 ? 'Surplus' : 'Deficit'} this month
                  </Typography>
                </ModernCard>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <ModernCard 
                  sx={{ 
                    p: 4, 
                    textAlign: 'center', 
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: savingsDisplay.status === 'neutral' || savingsDisplay.status === 'insufficient'
                        ? `linear-gradient(135deg, ${theme.palette.grey[300]}15, ${theme.palette.grey[400]}15)`
                        : savingsDisplay.status === 'overspent'
                        ? `linear-gradient(135deg, ${theme.palette.error.light}15, ${theme.palette.error.main}15)`
                        : savingsDisplay.status === 'excellent'
                        ? `linear-gradient(135deg, ${theme.palette.info.light}15, ${theme.palette.info.main}15)`
                        : `linear-gradient(135deg, ${theme.palette.warning.light}15, ${theme.palette.warning.main}15)`,
                      zIndex: 0,
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mx: 'auto',
                        mb: 2,
                        background: savingsDisplay.status === 'neutral' || savingsDisplay.status === 'insufficient'
                          ? `linear-gradient(135deg, ${theme.palette.grey[400]}, ${theme.palette.grey[600]})`
                          : savingsDisplay.status === 'overspent'
                          ? `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`
                          : savingsDisplay.status === 'excellent'
                          ? `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`
                          : `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                      }}
                    >
                      <Savings sx={{ fontSize: '1.8rem', color: 'white' }} />
                    </Avatar>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Savings Rate
                    </Typography>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 700, 
                        color: savingsDisplay.status === 'neutral' || savingsDisplay.status === 'insufficient'
                          ? theme.palette.text.secondary
                          : savingsDisplay.status === 'overspent'
                          ? theme.palette.error.main
                          : savingsDisplay.status === 'excellent'
                          ? theme.palette.info.main
                          : theme.palette.warning.main,
                        mb: 1,
                        fontSize: savingsDisplay.status === 'neutral' || savingsDisplay.status === 'insufficient' ? '1.75rem' : '2.125rem',
                      }}
                    >
                      {loading ? <Skeleton width={80} /> : savingsDisplay.text}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {savingsDisplay.subtitle}
                    </Typography>
                  </Box>
                </ModernCard>
              </Grid>
              </Grid>
            </Fade>

            {/* Reports Section with Animation */}
            {report ? (
              <Fade in={true} timeout={800} key={`reports-${dataKey}`}>
                <Grid container spacing={4}>
                <Grid item xs={12} lg={6}>
                  <ModernCard sx={{ height: 'fit-content' }}>
                    <Box sx={{ 
                      p: 3, 
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      background: `linear-gradient(135deg, ${theme.palette.error.light}10 0%, ${theme.palette.error.main}10 100%)`,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                          }}
                        >
                          <TrendingDown sx={{ color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                            Expense Analysis
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Detailed breakdown of your spending
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <ExpensesMonthlyReportsComponent
                        year={selectedYear}
                        month={selectedMonth}
                        report={report}
                      />
                    </Box>
                  </ModernCard>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <ModernCard sx={{ height: 'fit-content' }}>
                    <Box sx={{ 
                      p: 3, 
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      background: `linear-gradient(135deg, ${theme.palette.success.light}10 0%, ${theme.palette.success.main}10 100%)`,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                          }}
                        >
                          <TrendingUp sx={{ color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                            Income Analysis
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Overview of your revenue sources
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ p: 3 }}>
                      <IncomesMonthlyReportsComponent
                        year={selectedYear}
                        month={selectedMonth}
                        report={report}
                      />
                    </Box>
                  </ModernCard>
                </Grid>
                </Grid>
              </Fade>
            ) : (
              <ModernCard sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No data available
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No financial data found for {selectedMonth} {selectedYear}
                </Typography>
                <GradientButton variant="outlined" onClick={fetchReport}>
                  Refresh Data
                </GradientButton>
              </ModernCard>
            )}
          </Box>
        </Fade>
      </Container>

    </PageContainer>
  );
};

export default MonthlyReportsPage;
